/**
 * PMS API Service
 * Handles all API calls for PMS integration
 */

const API_BASE = '/api/tenants';

/**
 * Get JWT token from localStorage
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('jwt');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Get PMS connection status for a hotel
 */
export const getPMSStatus = async (brandId, hotelId) => {
  try {
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/status`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get PMS status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting PMS status:', error);
    throw error;
  }
};

/**
 * Connect PMS for a hotel
 */
export const connectPMS = async (brandId, hotelId, credentials) => {
  try {
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/connect`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          pms_provider: credentials.provider,
          api_key: credentials.apiKey,
          pms_property_id: credentials.propertyId,
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to connect PMS');
    }

    return await response.json();
  } catch (error) {
    console.error('Error connecting PMS:', error);
    throw error;
  }
};

/**
 * Test PMS connection (validates credentials)
 */
export const testPMSConnection = async (brandId, hotelId, credentials) => {
  try {
    // For now, we'll use the connect endpoint with a test flag
    // In the future, we might want a dedicated test endpoint
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/connect`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          pms_provider: credentials.provider,
          api_key: credentials.apiKey,
          pms_property_id: credentials.propertyId,
          test_only: true, // Flag to indicate this is just a test
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Connection test failed');
    }

    return await response.json();
  } catch (error) {
    console.error('Error testing PMS connection:', error);
    throw error;
  }
};

/**
 * Disconnect PMS for a hotel
 */
export const disconnectPMS = async (brandId, hotelId) => {
  try {
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/disconnect`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to disconnect PMS');
    }

    return await response.json();
  } catch (error) {
    console.error('Error disconnecting PMS:', error);
    throw error;
  }
};

/**
 * Trigger manual sync for a hotel
 */
export const triggerSync = async (brandId, hotelId, syncType = 'all') => {
  try {
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/sync`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sync_type: syncType, // 'all', 'rates', 'reservations', 'room_types'
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to trigger sync');
    }

    return await response.json();
  } catch (error) {
    console.error('Error triggering sync:', error);
    throw error;
  }
};

/**
 * Get sync history for a hotel
 */
export const getSyncHistory = async (brandId, hotelId, limit = 10) => {
  try {
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/sync-history?limit=${limit}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get sync history');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting sync history:', error);
    throw error;
  }
};

/**
 * Get webhook events for a hotel
 */
export const getWebhookEvents = async (brandId, hotelId, limit = 20) => {
  try {
    const response = await fetch(
      `${API_BASE}/brands/${brandId}/hotels/${hotelId}/pms/webhook-events?limit=${limit}`,
      {
        method: 'GET',
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to get webhook events');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting webhook events:', error);
    throw error;
  }
};

export default {
  getPMSStatus,
  connectPMS,
  testPMSConnection,
  disconnectPMS,
  triggerSync,
  getSyncHistory,
  getWebhookEvents,
};
